import type { en } from './en.js';
import type { ReviewState } from './index.js';

/**
 * Lao strings — ພາສາລາວ.
 *
 * ── Read this before editing ──
 *
 * The UI brief §5 forbids machine-translated UI strings, and the reason is
 * sound: a plausible-looking mistranslation is invisible, so it ships and
 * stays, whereas visibly-English text on a Lao screen gets noticed and fixed.
 * Agricultural vocabulary is where this bites hardest — "spadix", "copra" and
 * "crown cleaning" are specific terms, and a generic translation reads as
 * nonsense to a farmer.
 *
 * Every string below therefore carries its provenance in `LAO_REVIEW_STATE`:
 *
 *   `source`   taken verbatim from the client's own brief documents. Trusted.
 *   `draft`    written for the client to correct. NOT yet verified.
 *   `native`   reviewed and approved by a Lao speaker. Safe to ship.
 *
 * The `draft` entries exist because the client asked for a Lao interface and is
 * themselves a Lao speaker — they are the reviewer, which is a different
 * situation from a non-Lao team shipping unreviewed text. They are still drafts
 * until marked `native`, and `pendingReview()` lists them.
 *
 * Deliberately conservative: general UI vocabulary is drafted, and the
 * specialist agronomy terms are left to the reviewer rather than guessed at.
 */
export const lo: Partial<Record<keyof typeof en, string>> = {
  // ---- Sourced verbatim from the client's briefs (UI brief §7.10, §7.4d) ----
  'wo.result': 'ຜົນ',
  'wo.note': 'ໝາຍເຫດ',
  'wo.date': 'ວັນທີ',
  'tree.position': 'ຕົ້ນ',
  'wo.scanToRecord': 'ສະແກນເພື່ອບັນທຶກຜົນ',
  'wo.dateFormatHint': 'ວ/ດ/ປ — DD/MM/YYYY',
  'input.fertilise': 'ໃສ່ຝຸ່ນ',

  // ---- Navigation ----
  'app.name': 'ສົມສະໜຸກ ໝາກພ້າວ',
  'nav.dashboard': 'ໜ້າຫຼັກ',
  'nav.positions': 'ຕຳແໜ່ງຕົ້ນໄມ້',
  'nav.trees': 'ຕົ້ນໄມ້',
  'nav.workOrders': 'ໃບສັ່ງງານ',
  'nav.issues': 'ບັນຫາ',
  'nav.harvest': 'ການເກັບກ່ຽວ',
  'nav.inventory': 'ສາງວັດຖຸ',
  'nav.reports': 'ລາຍງານ',
  'nav.admin': 'ຕັ້ງຄ່າ',
  'nav.site': 'ສວນ',

  // ---- Auth ----
  'auth.login': 'ເຂົ້າສູ່ລະບົບ',
  'auth.logout': 'ອອກຈາກລະບົບ',
  'auth.email': 'ອີເມວ',
  'auth.password': 'ລະຫັດຜ່ານ',
  'auth.staySignedIn': 'ຈື່ຂ້ອຍໄວ້',
  'auth.invalidCredentials': 'ອີເມວ ຫຼື ລະຫັດຜ່ານບໍ່ຖືກຕ້ອງ',
  'auth.sessionExpired': 'ໝົດເວລາໃຊ້ງານ ກະລຸນາເຂົ້າສູ່ລະບົບໃໝ່',
  'auth.offline': 'ບໍ່ມີສັນຍານອິນເຕີເນັດ',

  // ---- Common actions ----
  'action.save': 'ບັນທຶກ',
  'action.cancel': 'ຍົກເລີກ',
  'action.edit': 'ແກ້ໄຂ',
  'action.delete': 'ລຶບ',
  'action.search': 'ຄົ້ນຫາ',
  'action.print': 'ພິມ',
  'action.export': 'ສົ່ງອອກ',
  'action.add': 'ເພີ່ມ',
  'action.retry': 'ລອງໃໝ່',
  'action.clearFilters': 'ລ້າງຕົວກັ່ນຕອງ',

  // ---- Save state ----
  'state.saving': 'ກຳລັງບັນທຶກ…',
  'state.saved': 'ບັນທຶກແລ້ວ',
  'state.saveFailed': 'ບັນທຶກບໍ່ສຳເລັດ — ກົດເພື່ອລອງໃໝ່',
  'state.loading': 'ກຳລັງໂຫຼດ…',

  // ---- Tree facts ----
  'tree.one': 'ຕົ້ນໄມ້',
  'tree.variety': 'ພັນ',
  'tree.age': 'ອາຍຸ',
  'tree.plantedOn': 'ວັນທີປູກ',
  'tree.status': 'ສະຖານະ',
  'tree.healthScore': 'ສຸຂະພາບ',
  'tree.lastHarvest': 'ເກັບກ່ຽວຄັ້ງລ່າສຸດ',
  'tree.openIssues': 'ບັນຫາທີ່ຍັງຄ້າງ',
  'tree.legacyNumber': 'ເລກເກົ່າ',

  // ---- Timeline ----
  'timeline.title': 'ປະຫວັດ',
  'timeline.addPlaceholder': 'ເພີ່ມບັນທຶກ…',
  'timeline.filter.all': 'ທັງໝົດ',
  'timeline.filter.work': 'ວຽກ',
  'timeline.filter.health': 'ສຸຂະພາບ',
  'timeline.filter.harvest': 'ເກັບກ່ຽວ',
  'timeline.filter.photos': 'ຮູບພາບ',
  'timeline.filter.notes': 'ໝາຍເຫດ',

  // ---- Work orders ----
  'wo.one': 'ໃບສັ່ງງານ',
  'wo.title': 'ຫົວຂໍ້',
  'wo.assignee': 'ຜູ້ຮັບຜິດຊອບ',
  'wo.priority': 'ຄວາມສຳຄັນ',
  'wo.opened': 'ວັນທີເປີດ',
  'wo.due': 'ກຳນົດສົ່ງ',
  'wo.completed': 'ສຳເລັດ',
  'wo.progress': 'ຄວາມຄືບໜ້າ',
  'wo.done': 'ແລ້ວ',
  'wo.skipped': 'ຂ້າມ',
  'wo.printPdf': 'ພິມ PDF',
  'wo.workerSignature': 'ລາຍເຊັນຜູ້ເຮັດວຽກ',
  'wo.dateCompleted': 'ວັນທີສຳເລັດ',
  'wo.totalLines': 'ລວມທັງໝົດ',

  // ---- Missing keys, completed at the client's request ----
  'auth.accountLocked': 'ບັນຊີນີ້ຖືກລັອກ ກະລຸນາຕິດຕໍ່ຜູ້ດູແລລະບົບ',
  'state.offlineQueued': 'ຈະສົ່ງເມື່ອມີສັນຍານອິນເຕີເນັດຄືນ',
  'tree.lifetimeNuts': 'ຈຳນວນໝາກສະສົມ',
  'timeline.dateFromPhoto': 'ວັນທີຈາກຮູບພາບ',
  'timeline.empty': 'ຍັງບໍ່ມີບັນທຶກ ເພີ່ມບັນທຶກທຳອິດເລີຍ',
  'timeline.corrected': 'ແກ້ໄຂແລ້ວ',
  'timeline.hidden': 'ບັນທຶກຖືກເຊື່ອງໄວ້',
  'timeline.gpsMismatch': 'ຮູບຖ່າຍຫ່າງຈາກຕົ້ນນີ້ {distance} ແມັດ',
  'timeline.filter.growth': 'ການເຕີບໂຕ',
  'timeline.filter.inputs': 'ຝຸ່ນ ແລະ ຢາ',
  'wo.number': 'ເລກທີໃບສັ່ງງານ',
  'wo.taskType': 'ປະເພດວຽກ',
  'wo.daysOpen': 'ຈຳນວນມື້ຄ້າງ',
  'wo.blocked': 'ຕິດຂັດ',
  'wo.managerVerification': 'ການກວດສອບຂອງຜູ້ຈັດການ',
  'wo.recordedCount': 'ບັນທຶກແລ້ວ {done} / {total}',
  'input.ratePerPalm': 'ອັດຕາຕໍ່ຕົ້ນ',
  'input.reentryInterval': 'ໄລຍະຫ້າມເຂົ້າສວນ',
  'input.preHarvestInterval': 'ໄລຍະງົດເກັບກ່ຽວ',

  // ---- Dashboard ----
  'dash.totalPlanted': 'ປູກທັງໝົດ',
  'dash.haveFlowered': 'ອອກດອກແລ້ວ',
  'dash.producing': 'ກຳລັງໃຫ້ໝາກ',
  'dash.workTasks': 'ວຽກງານ',
  'dash.harvested': 'ເກັບກ່ຽວແລ້ວ',
  'dash.recentActivity': 'ກິດຈະກຳຫຼ້າສຸດ',
  'dash.entries': '{n} ລາຍການ',

  // ---- Positions list ----
  'pos.add': 'ເພີ່ມຕຳແໜ່ງ',
  'pos.zone': 'ໂຊນ',
  'pos.allZones': 'ທຸກໂຊນ',
  'pos.tag': 'ປ້າຍ',
  'pos.plant': 'ປູກ',
  'pos.vacant': 'ຫວ່າງ',
  'pos.growth': 'ການເຕີບໂຕ',
  'pos.growth.flowered': 'ອອກດອກແລ້ວ',
  'pos.growth.harvested': 'ເກັບກ່ຽວຄັ້ງທຳອິດແລ້ວ',
  'pos.growth.notFlowered': 'ຍັງບໍ່ອອກດອກ',
  'pos.status.active': 'ມີຕົ້ນໄມ້',
  'pos.status.vacant': 'ຫວ່າງ',
  'pos.status.reserved': 'ຈອງໄວ້',
  'pos.status.retired': 'ຍົກເລີກ',
  'pos.statusAny': 'ທັງໝົດ',
  'action.loadMore': 'ໂຫຼດເພີ່ມ',

  // ---- Errors ----
  'error.generic': 'ມີບາງຢ່າງຜິດພາດ ກະລຸນາລອງໃໝ່',
  'error.notFound': 'ບໍ່ພົບຂໍ້ມູນ',
  'error.forbidden': 'ທ່ານບໍ່ມີສິດເຮັດສິ່ງນີ້',
  'error.validation': 'ກະລຸນາກວດຄືນຊ່ອງທີ່ໝາຍໄວ້',
};

/**
 * Provenance of every Lao string above.
 *
 * `draft` means written for the client to correct, not verified. The CI check
 * in `scripts/check-i18n.ts` refuses a production build while any string the
 * client has marked as needing review is still outstanding.
 */
export const LAO_REVIEW_STATE: Partial<Record<keyof typeof en, ReviewState>> = Object.fromEntries(
  Object.keys(lo).map((key) => [
    key,
    // The seven from the client's own documents are trusted; the rest are drafts.
    [
      'wo.result', 'wo.note', 'wo.date', 'tree.position',
      'wo.scanToRecord', 'wo.dateFormatHint', 'input.fertilise',
    ].includes(key)
      ? 'source'
      : 'draft',
  ]),
) as Partial<Record<keyof typeof en, ReviewState>>;

/** Strings still awaiting a Lao speaker's approval. Must be empty to ship. */
export function pendingReview(): string[] {
  return Object.entries(LAO_REVIEW_STATE)
    .filter(([, state]) => state === 'draft' || state === 'machine')
    .map(([key]) => key);
}
